/**
 * HTML to Image conversion utilities
 * Uses html2canvas for client-side image generation
 */

export interface ConversionOptions {
  width?: number;
  height?: number;
  scale?: number;
  format?: 'png' | 'jpeg' | 'webp';
  quality?: number;
  backgroundColor?: string;
}

export interface ConversionResult {
  success: boolean;
  dataUrl?: string;
  blob?: Blob;
  error?: string;
}

export class HTMLToImageConverter {
  /**
   * Convert HTML string to image using an iframe
   */
  static async convertHTMLToImage(
    html: string,
    options: ConversionOptions = {}
  ): Promise<ConversionResult> {
    try {
      // Dynamic import of html2canvas to avoid SSR issues
      const html2canvas = (await import('html2canvas')).default;

      // Create a temporary iframe to render the HTML
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.left = '-9999px';
      iframe.style.top = '-9999px';
      iframe.style.width = `${options.width || 400}px`;
      iframe.style.height = `${options.height || 600}px`;
      iframe.style.border = 'none';

      document.body.appendChild(iframe);

      return new Promise((resolve) => {
        iframe.onload = async () => {
          try {
            if (!iframe.contentDocument || !iframe.contentWindow) {
              throw new Error('Failed to access iframe content');
            }

            // Wait a bit for fonts and styles to load
            await new Promise(resolve => setTimeout(resolve, 500));

            const canvas = await html2canvas(iframe.contentDocument.body, {
              width: options.width || 400,
              height: options.height || 600,
              scale: options.scale || 2,
              backgroundColor: options.backgroundColor || null,
              useCORS: true,
              allowTaint: true,
              foreignObjectRendering: true
            });

            // Convert canvas to blob
            canvas.toBlob(
              (blob) => {
                document.body.removeChild(iframe);
                
                if (blob) {
                  const dataUrl = canvas.toDataURL(
                    `image/${options.format || 'png'}`,
                    options.quality || 0.9
                  );
                  
                  resolve({
                    success: true,
                    dataUrl,
                    blob
                  });
                } else {
                  resolve({
                    success: false,
                    error: 'Failed to create blob from canvas'
                  });
                }
              },
              `image/${options.format || 'png'}`,
              options.quality || 0.9
            );
          } catch (error) {
            document.body.removeChild(iframe);
            resolve({
              success: false,
              error: error instanceof Error ? error.message : 'Conversion failed'
            });
          }
        };

        // Set iframe content
        iframe.contentDocument?.open();
        iframe.contentDocument?.write(html);
        iframe.contentDocument?.close();
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to load html2canvas'
      };
    }
  }

  /**
   * Convert HTML element to image
   */
  static async convertElementToImage(
    element: HTMLElement,
    options: ConversionOptions = {}
  ): Promise<ConversionResult> {
    try {
      const html2canvas = (await import('html2canvas')).default;

      const canvas = await html2canvas(element, {
        width: options.width || element.offsetWidth,
        height: options.height || element.offsetHeight,
        scale: options.scale || 2,
        backgroundColor: options.backgroundColor || null,
        useCORS: true,
        allowTaint: true,
        foreignObjectRendering: true
      });

      return new Promise((resolve) => {
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const dataUrl = canvas.toDataURL(
                `image/${options.format || 'png'}`,
                options.quality || 0.9
              );
              
              resolve({
                success: true,
                dataUrl,
                blob
              });
            } else {
              resolve({
                success: false,
                error: 'Failed to create blob from canvas'
              });
            }
          },
          `image/${options.format || 'png'}`,
          options.quality || 0.9
        );
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Conversion failed'
      };
    }
  }

  /**
   * Download image from blob
   */
  static downloadImage(blob: Blob, filename: string = 'cover.png'): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Copy image to clipboard
   */
  static async copyImageToClipboard(blob: Blob): Promise<boolean> {
    try {
      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([
          new ClipboardItem({
            [blob.type]: blob
          })
        ]);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to copy image to clipboard:', error);
      return false;
    }
  }

  /**
   * Get image dimensions from HTML
   */
  static getImageDimensions(html: string): { width: number; height: number } {
    // Try to extract dimensions from CSS or default to standard cover size
    const widthMatch = html.match(/width:\s*(\d+)px/);
    const heightMatch = html.match(/height:\s*(\d+)px/);
    
    return {
      width: widthMatch ? parseInt(widthMatch[1]) : 400,
      height: heightMatch ? parseInt(heightMatch[1]) : 600
    };
  }
}
